import { useMemo, useState } from 'react';
import { InlineNotification, Link, Modal } from '@carbon/react';
import {
  DataTable,
  type DataTableColumnDef,
  type DataTableSelectionState,
} from 'components/data-table';
import { ValidityStatusIndicator } from 'components/structure-grids';
import { Date as FormattedDate } from 'components/date';
import { format0 } from 'utils/number';
import type { CollectionGrid } from './use-structure-grid-collections';
import styles from './add-grids-modal.module.scss';

/** What a column shows where a grid has no value for it. */
const MISSING = '--';

/**
 * A grid's cell as the Structure Grid Management table writes it: width by height, in
 * feet. A grid that never recorded them, or recorded something under a foot, has no
 * cell to state.
 *
 * @param {number | null} width - Cell width in feet.
 * @param {number | null} height - Cell height in feet.
 * @return {string} The cell size, or the missing marker.
 */
const cellSizeOf = (width: number | null, height: number | null): string => {
  if (width === null || height === null || width < 1 || height < 1) {
    return MISSING;
  }
  return `${format0(Math.round(width))} ft x ${format0(Math.round(height))} ft`;
};

/**
 * A grid as the picker's table holds it. `DataTable` is generic over an index
 * signature, which an interface does not satisfy on its own.
 */
interface GridRow extends Record<string, unknown> {
  id: string;
  href: string;
  interval: string;
  gridSurface: string;
  basin: string;
  cellSize: string;
  owner: string;
  source: string;
  createdAt: string | null;
  validity: string;
}

export interface AddGridsModalProps {
  /** The grids the section crosses that are not on it yet. */
  candidates: CollectionGrid[];
  /**
   * True while the answer to "which grids does this line cross" is still coming. The
   * table waits for it rather than showing the collection's own stack and swapping it
   * out underneath the reader.
   */
  loading?: boolean;
  /** A failed lookup, said plainly instead of as an empty table. */
  error?: Error | null;
  onClose: () => void;
  onAdd: (gridIds: number[]) => void;
}

/**
 * The grid picker.
 *
 * A table rather than a dropdown: each grid carries a row's worth of attributes worth
 * comparing before it goes on the plot, and a type-ahead menu can only honestly show
 * one line per row. Multi-select, so building a stack is one trip.
 *
 * The app's own `DataTable` draws it — the same sortable columns, search and skeleton
 * every other table in the product has, and the same treatment a grid gets on the
 * Structure Grids page it came from.
 *
 * The columns are the Structure Grid Management table's, in its order, so a grid reads
 * the same in both places. Average thickness is not among them: it belongs to a grid's
 * place in one collection's ordering, and this table offers grids from any collection
 * the line crosses.
 *
 * Paginated, because a long line across a well-mapped basin crosses more grids than a
 * modal can hold at once.
 *
 * @param {AddGridsModalProps} props - Component props.
 * @return {JSX.Element} The modal.
 */
export const AddGridsModal = ({
  candidates,
  loading = false,
  error = null,
  onClose,
  onAdd,
}: AddGridsModalProps): JSX.Element => {
  const [chosen, setChosen] = useState<string[]>([]);

  // Shallow to deep, so the list reads like the stack itself. Sorting a column takes
  // over from there.
  const rows = useMemo<GridRow[]>(
    () =>
      [...candidates]
        .sort(
          (a, b) =>
            (a.geologicIntervalPositionalIndex ?? Number.POSITIVE_INFINITY) -
            (b.geologicIntervalPositionalIndex ?? Number.POSITIVE_INFINITY)
        )
        .map((grid) => ({
          id: String(grid.structureGridId),
          href: grid.href,
          interval: grid.geologicInterval,
          gridSurface: grid.gridSurface,
          basin: grid.basinName ?? MISSING,
          cellSize: cellSizeOf(grid.cellWidthFeet, grid.cellHeightFeet),
          owner: grid.owner ?? MISSING,
          source: grid.source ?? MISSING,
          createdAt: grid.createdAt,
          validity: grid.validity,
        })),
    [candidates]
  );

  const columns = useMemo<DataTableColumnDef<GridRow>[]>(
    () => [
      {
        id: 'interval',
        accessorKey: 'interval',
        header: 'Geologic Interval',
        meta: { label: 'Geologic Interval' },
      },
      {
        id: 'gridSurface',
        accessorKey: 'gridSurface',
        header: 'Grid Surface',
        meta: { label: 'Grid Surface' },
        // Linked the way a well name is in the well picker: the row stands for a
        // record, and the record is worth reaching.
        cell: ({ row }) => (
          <Link href={row.original.href}>{row.original.gridSurface}</Link>
        ),
      },
      {
        id: 'basin',
        accessorKey: 'basin',
        header: 'Basin',
        meta: { label: 'Basin' },
      },
      {
        id: 'cellSize',
        accessorKey: 'cellSize',
        header: 'Grid Cell Size (W x H)',
        meta: { label: 'Grid Cell Size (W x H)' },
        // Wide enough for its own heading: the values are short, the name is not, and a
        // truncated heading is the column nobody can identify.
        size: 210,
      },
      {
        id: 'owner',
        accessorKey: 'owner',
        header: 'Data Owner',
        meta: { label: 'Data Owner' },
      },
      {
        id: 'source',
        accessorKey: 'source',
        header: 'Data Source',
        meta: { label: 'Data Source' },
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Created At',
        meta: { label: 'Created At' },
        // Sorted on the ISO string the backend sent, which orders correctly, and shown in
        // the reader's own locale by the component the rest of the app uses for a date.
        cell: ({ row }) => (
          <FormattedDate date={row.original.createdAt} fallback={MISSING} />
        ),
      },
      {
        id: 'validity',
        accessorKey: 'validity',
        header: 'Validity',
        meta: { label: 'Validity' },
        cell: ({ row }) => (
          <ValidityStatusIndicator status={row.original.validity} />
        ),
      },
    ],
    []
  );

  const selection = useMemo<DataTableSelectionState>(
    () => ({ selectAll: false, toggledNodes: chosen }),
    [chosen]
  );

  return (
    <Modal
      open
      className={styles.modal}
      modalHeading="Add structure grids"
      modalLabel="Cross Section"
      primaryButtonText={
        chosen.length > 0
          ? `Add ${chosen.length} grid${chosen.length === 1 ? '' : 's'}`
          : 'Add grids'
      }
      secondaryButtonText="Cancel"
      primaryButtonDisabled={chosen.length === 0}
      size="lg"
      onRequestClose={onClose}
      onSecondarySubmit={onClose}
      onRequestSubmit={() => onAdd(chosen.map((id) => Number(id)))}
    >
      <p className={styles.intro}>
        Every grid the section crosses, whichever collection it belongs to.
        Grids already on the section aren&apos;t listed.
      </p>
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Could not read the grids this section crosses"
          subtitle={error.message}
        />
      )}
      <div className={styles.table}>
        <DataTable<GridRow>
          data={rows}
          columns={columns}
          getRowId={(row, index) => String(row.id ?? index)}
          isLoading={loading}
          // Client-side: every candidate is already here, so paging is the table's own.
          scroll={{ mode: 'paginated' }}
          // Ten to a page, not the app-wide hundred: a page taller than the modal is a
          // scroll bar pretending to be a pager.
          initialTableState={{ pageSize: 10 }}
          controlledState={{ selection }}
          onSelectionChange={(state) =>
            setChosen(
              state.selection.selectAll
                ? rows
                    .map((row) => row.id)
                    .filter((id) => !state.selection.toggledNodes.includes(id))
                : state.selection.toggledNodes
            )
          }
          searchPlaceholder="Search interval, grid surface, basin, owner, or source"
          config={{
            // Every candidate is already here, so searching and sorting are the table's
            // own to do. The rest of the toolbar has nothing to act on in a modal.
            selectionType: 'multi',
            columnFilters: false,
            columnVisibility: false,
            columnReordering: false,
            columnResizing: false,
            columnPinning: false,
          }}
        />
      </div>
    </Modal>
  );
};
